import { useEffect, useState, useCallback } from 'react';
import { WORKER_API_URL, friendlyError, type AuthState } from '@/config';
import { getSession, logout, type SessionInfo } from '@/api';
import LoginForm from '@/components/LoginForm';
import Dashboard from '@/components/Dashboard';

type View = 'loading' | 'login' | 'dashboard';

/** Root app: resolves the persisted session and routes to login or dashboard. */
export default function App() {
  const [view, setView] = useState<View>('loading');
  const [auth, setAuth] = useState<AuthState | null>(null);
  const [error, setError] = useState<string | null>(null);

  const checkSession = useCallback(async () => {
    setView('loading');
    setError(null);
    try {
      const { ok, status, data } = await getSession();
      if (ok && data.authenticated && data.csrfToken) {
        setAuth({ user: data.user!, role: (data.role as AuthState['role']) ?? 'viewer', csrfToken: data.csrfToken });
        setView('dashboard');
      } else if (status === 503) {
        setError('Backend is not configured.');
        setView('loading');
      } else {
        setAuth(null);
        setView('login');
      }
    } catch {
      setError('Could not reach the backend.');
      setView('loading');
    }
  }, []);

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  const handleLogin = useCallback((a: AuthState) => {
    setAuth(a);
    setError(null);
    setView('dashboard');
  }, []);

  const handleLogout = useCallback(async () => {
    try { await logout(); } catch {}
    setAuth(null);
    setView('login');
  }, []);

  if (!WORKER_API_URL) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
        <div className="max-w-md rounded-xl bg-amber-50 p-6 text-amber-900">
          <h1 className="text-lg font-semibold">Backend URL not configured</h1>
          <p className="mt-2">
            Set the <code className="rounded bg-amber-100 px-1.5 py-0.5">VITE_WORKER_API_URL</code> environment variable to your deployed Worker URL.
          </p>
        </div>
      </div>
    );
  }

  if (view === 'loading' || view === 'login' && !auth) {
    if (error) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
          <div className="max-w-md rounded-xl bg-rose-50 p-6 text-rose-900">
            <h1 className="text-lg font-semibold">Connection error</h1>
            <p className="mt-2">{error}</p>
            <button className="btn mt-4 w-full bg-slate-900 text-white hover:bg-slate-800" onClick={checkSession}>Retry</button>
          </div>
        </div>
      );
    }
    return <LoginForm onLogin={handleLogin} onLoginError={setError} />;
  }

  return <Dashboard auth={auth!} onLogout={handleLogout} onUnauthenticated={() => setView('login')} />;
}
